<template>
  <a-tooltip
    v-if="isOverflow"
    :title="text"
    :overlay-class-name="tooltipClassName"
    :placement="placement"
    :color="tooltipColor"
  >
    <span 
      :class="['text-ellipsis', customClass, { multiline: lineClamp > 0 }]" 
      :style="[textStyle, lineClamp > 0 ? { '-webkit-line-clamp': lineClamp } : {}]"
    >
      {{ displayedText }}
    </span>
  </a-tooltip>
  <span 
    v-else 
    :class="['text-ellipsis', customClass, { multiline: lineClamp > 0 }]" 
    :style="[textStyle, lineClamp > 0 ? { '-webkit-line-clamp': lineClamp } : {}]"
  >
    {{ text }}
  </span>
</template>

<script>
export default {
  name: 'TextEllipsis',
  props: {
    // 文本内容
    text: {
      type: String,
      default: ''
    },
    // 最大显示长度（支持字符数或像素宽度）
    maxLength: {
      type: [Number, String],
      default: 20
    },
    // 长度计算方式：'char'按字符数，'width'按像素宽度
    lengthType: {
      type: String,
      default: 'char', // 'char' | 'width'
      validator: (value) => ['char', 'width'].includes(value)
    },
    // 中文字符长度计算比例（一个中文字符相当于几个英文字符）
    chineseRatio: {
      type: Number,
      default: 2
    },
    // 自定义样式类
    customClass: {
      type: String,
      default: ''
    },
    // 文本行数限制（CSS实现多行省略）
    lineClamp: {
      type: Number,
      default: 0
    },
    // 气泡提示位置
    placement: {
      type: String,
      default: 'top'
    },
    // 气泡背景色
    tooltipColor: {
      type: String,
      default: ''
    },
    // 自定义气泡样式类
    tooltipClassName: {
      type: String,
      default: 'custom-tooltip'
    },
    // 文本样式
    textStyle: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    // 计算实际字符长度（考虑中英文差异）
    actualLength() {
      if (this.lengthType === 'width') {
        return this.calculateTextWidth(this.text);
      }
      
      let length = 0;
      for (let i = 0; i < this.text.length; i++) {
        const char = this.text[i];
        // 判断是否为中文字符
        if (this.isChineseChar(char)) {
          length += this.chineseRatio;
        } else {
          length += 1;
        }
      }
      return length;
    },
    
    isOverflow() {
      if (this.lengthType === 'width') {
        return this.actualLength > parseInt(this.maxLength);
      }
      return this.actualLength > this.maxLength;
    },
    
    displayedText() {
      if (!this.isOverflow) {
        return this.text;
      }
      
      if (this.lengthType === 'width') {
        return this.truncateByWidth(this.text, parseInt(this.maxLength));
      } else {
        return this.truncateByCharCount(this.text, this.maxLength);
      }
    }
  },
  methods: {
    // 判断是否为中文字符
    isChineseChar(char) {
      const charCode = char.charCodeAt(0);
      // 中文字符的Unicode范围
      return (charCode >= 0x4E00 && charCode <= 0x9FFF) ||
             (charCode >= 0x3400 && charCode <= 0x4DBF) ||
             (charCode >= 0x20000 && charCode <= 0x2A6DF) ||
             (charCode >= 0x2A700 && charCode <= 0x2B73F) ||
             (charCode >= 0x2B740 && charCode <= 0x2B81F) ||
             (charCode >= 0x2B820 && charCode <= 0x2CEAF) ||
             (charCode >= 0xF900 && charCode <= 0xFAFF) ||
             (charCode >= 0x2F800 && charCode <= 0x2FA1F);
    },
    
    // 按字符数截断文本
    truncateByCharCount(text, maxLength) {
      let currentLength = 0;
      let result = '';
      
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        let charLength;
        
        if (this.isChineseChar(char)) {
          charLength = this.chineseRatio;
        } else {
          charLength = 1;
        }
        
        if (currentLength + charLength > maxLength) {
          break;
        }
        
        result += char;
        currentLength += charLength;
      }
      
      return result + '...';
    },
    
    // 计算文本宽度（近似值）
    calculateTextWidth(text) {
      // 创建一个临时元素来计算文本宽度
      const span = document.createElement('span');
      span.style.visibility = 'hidden';
      span.style.position = 'absolute';
      span.style.whiteSpace = 'nowrap';
      span.style.font = this.getComputedFont();
      span.textContent = text;
      
      document.body.appendChild(span);
      const width = span.offsetWidth;
      document.body.removeChild(span);
      
      return width;
    },
    
    // 获取计算后的字体样式
    getComputedFont() {
      // 这里可以更精确地获取实际应用的字体样式
      // 简化实现，返回默认字体
      return '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial';
    },
    
    // 按宽度截断文本
    truncateByWidth(text, maxWidth) {
      if (this.calculateTextWidth(text) <= maxWidth) {
        return text;
      }
      
      let left = 0;
      let right = text.length;
      let result = '';
      
      // 使用二分查找找到合适的截断位置
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const testText = text.substring(0, mid) + '...';
        const width = this.calculateTextWidth(testText);
        
        if (width <= maxWidth) {
          result = testText;
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }
      
      return result;
    }
  }
};
</script>

<style scoped>
.text-ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
  vertical-align: middle;
}

/* 多行文本省略支持 */
.text-ellipsis.multiline {
  white-space: normal;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 1;
}
</style>

<style>
/* 全局气泡样式 */
.custom-tooltip .ant-tooltip-inner {
  max-width: 400px;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.4;
}
</style>